import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Paper, Stack, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Snackbar, Card, CardContent,
  Dialog, DialogContent, DialogActions, LinearProgress,
} from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import HelpIcon from '@mui/icons-material/Help';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import { getUserData } from '../../services/storageService';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-c-0mxm.onrender.com/api';

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use((config) => {
  const user = getUserData();
  if (user) config.headers.Authorization = `Bearer ${user.userId || user.token}`;
  return config;
});

const AppVersionManagement = ({ isMobile }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tracking, setTracking] = useState(null);
  const [versionConfig, setVersionConfig] = useState({
    latestVersion: '',
    minimumVersion: '1.0.0',
    updateMessage: 'Có bản cập nhật mới! Vui lòng mở TestFlight để cập nhật.',
    forceUpdateMessage: 'Vui lòng mở TestFlight để cập nhật phiên bản mới nhất.',
  });
  const [editDialog, setEditDialog] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [versionRes, trackingRes] = await Promise.all([
        api.get('/system-settings/public/app-version'),
        api.get('/system-settings/admin/version-tracking'),
      ]);
      if (versionRes.data?.data) {
        setVersionConfig(versionRes.data.data);
      }
      if (trackingRes.data?.success) {
        setTracking(trackingRes.data.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!versionConfig.latestVersion) {
      setNotification({ open: true, message: 'Nhập phiên bản mới nhất', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/system-settings/admin/app-version', versionConfig);
      if (res.data.success) {
        setNotification({ open: true, message: 'Đã cập nhật version', severity: 'success' });
        setEditDialog(false);
        fetchData();
      }
    } catch (e) {
      setNotification({ open: true, message: e.response?.data?.message || 'Lỗi', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  const stats = tracking?.stats || { total: 0, upToDate: 0, outdated: 0, unknown: 0 };
  const users = tracking?.users || [];
  const percent = stats.total > 0 ? Math.round((stats.upToDate / stats.total) * 100) : 0;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#1e293b">
            Quản lý phiên bản App
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tạo thông báo cập nhật và theo dõi version user đang dùng
          </Typography>
        </Box>
        <Button
          variant="contained" startIcon={<EditIcon />}
          onClick={() => setEditDialog(true)}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#1e3a8a' }}
        >
          Cập nhật version
        </Button>
      </Stack>

      {/* Stats cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Card sx={{ minWidth: 160, borderRadius: 2, border: '1px solid #e0f2fe' }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Version mới nhất</Typography>
            <Typography variant="h5" fontWeight={700} color="#1e3a8a">
              {tracking?.latestVersion || versionConfig.latestVersion || '—'}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140, borderRadius: 2, border: '1px solid #dcfce7' }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Đã cập nhật</Typography>
            <Stack direction="row" alignItems="baseline" spacing={0.5}>
              <Typography variant="h5" fontWeight={700} color="#16a34a">{stats.upToDate}</Typography>
              <Typography variant="body2" color="text.secondary">/{stats.total}</Typography>
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140, borderRadius: 2, border: '1px solid #fef3c7' }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Chưa cập nhật</Typography>
            <Typography variant="h5" fontWeight={700} color="#d97706">{stats.outdated}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140, borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Chưa mở app</Typography>
            <Typography variant="h5" fontWeight={700} color="#94a3b8">{stats.unknown}</Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Progress bar */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>Tiến độ cập nhật</Typography>
          <Typography variant="subtitle2" fontWeight={700} color="#1e3a8a">{percent}%</Typography>
        </Stack>
        <LinearProgress
          variant="determinate" value={percent}
          sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0',
            '& .MuiLinearProgress-bar': { bgcolor: percent === 100 ? '#16a34a' : '#2563eb', borderRadius: 4 }
          }}
        />
      </Paper>

      {/* User table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f1f5f9' }}>
              <TableCell sx={{ fontWeight: 700 }}>Nhân viên</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Version</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Trạng thái</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Lần cuối mở app</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Chưa có dữ liệu</Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map(u => (
                <TableRow key={u.userId} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PhoneIphoneIcon sx={{ fontSize: 18, color: '#64748b' }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{u.fullName}</Typography>
                        <Typography variant="caption" color="text.secondary">{u.username}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {u.currentVersion ? (
                      <Chip label={`v${u.currentVersion}`} size="small" sx={{
                        fontWeight: 700,
                        bgcolor: u.isUpToDate ? '#dcfce7' : '#fef3c7',
                        color: u.isUpToDate ? '#16a34a' : '#92400e',
                      }} />
                    ) : (
                      <Chip label="Chưa rõ" size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {u.isUpToDate ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                        <Typography variant="caption" fontWeight={600} color="#16a34a">Mới nhất</Typography>
                      </Stack>
                    ) : u.currentVersion ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <WarningIcon sx={{ fontSize: 16, color: '#d97706' }} />
                        <Typography variant="caption" fontWeight={600} color="#d97706">Cần cập nhật</Typography>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <HelpIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                        <Typography variant="caption" fontWeight={600} color="#94a3b8">Chưa mở app</Typography>
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{formatDate(u.lastSeen)}</Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit version dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #059669 100%)', px: 3, py: 2 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>Cập nhật phiên bản App</Typography>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Khi tạo version mới, user mở app sẽ thấy thông báo cập nhật qua TestFlight.
          </Alert>
          <TextField
            fullWidth size="small" label="Phiên bản mới nhất *" placeholder="1.2.0"
            value={versionConfig.latestVersion}
            onChange={(e) => setVersionConfig({ ...versionConfig, latestVersion: e.target.value })}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth size="small" label="Phiên bản tối thiểu (bắt buộc cập nhật)" placeholder="1.0.0"
            value={versionConfig.minimumVersion}
            onChange={(e) => setVersionConfig({ ...versionConfig, minimumVersion: e.target.value })}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            helperText="User dưới version này sẽ bị bắt buộc cập nhật, không dùng app được"
          />
          <TextField
            fullWidth size="small" label="Tin nhắn cập nhật" multiline rows={2}
            value={versionConfig.updateMessage}
            onChange={(e) => setVersionConfig({ ...versionConfig, updateMessage: e.target.value })}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth size="small" label="Tin nhắn bắt buộc cập nhật" multiline rows={2}
            value={versionConfig.forceUpdateMessage}
            onChange={(e) => setVersionConfig({ ...versionConfig, forceUpdateMessage: e.target.value })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditDialog(false)} sx={{ borderRadius: 2 }}>Hủy</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{ borderRadius: 2, bgcolor: '#1e3a8a', fontWeight: 600 }}
          >
            Lưu & thông báo
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open} autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={notification.severity} sx={{ borderRadius: 2 }}>{notification.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AppVersionManagement;
